import { CommonModule } from '@angular/common';
import { Component, ContentChild, TemplateRef, Input, ElementRef, ViewChild, AfterViewInit, OnDestroy, HostListener, ChangeDetectorRef, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { WidgetEdit } from '../widget-edit/widget-edit';
import { OverlayModule, CdkOverlayOrigin } from '@angular/cdk/overlay';

@Component({
    selector: 'app-carousel-slider',
    standalone: true,
    imports: [CommonModule, WidgetEdit, OverlayModule],
    templateUrl: './carousel-slider.html',
    styleUrl: './carousel-slider.scss',
})
export class CarouselSliderComponent implements AfterViewInit, OnDestroy, OnChanges {
    @Input() items: any[] = [];
    @Input() title: string = '';
    @Input() showCount: boolean = true;
    @Input() isEditMode: boolean = false;
    @Input() isAddMode: boolean = false;
    @Output() addWidget = new EventEmitter<void>();
    @Input() index: number = 0;
    @Input() activeDropdownIndex: number | null = null;
    @Input() getAvailableSizes!: (index: number) => Array<{ value: string; label: string }>;
    @Input() toggleSizeDropdown!: (index: number, event: Event) => void;
    @Input() changeWidgetSize!: (index: number, size: any) => void;
    @Input() removeWidget!: (index: number) => void;
    @ContentChild('cardTemplate') cardTemplate!: TemplateRef<any>;


    @ViewChild('sliderContainer') sliderContainer!: ElementRef;

    currentIndex: number = 0;
    sliderHeight: number = 0;
    private itemOffsets: number[] = [];
    private resizeObserver: ResizeObserver | null = null;

    constructor(private cdr: ChangeDetectorRef) { }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['items']) {
            // Delay measurement until DOM is rendered
            setTimeout(() => this.updateSliderHeight(), 0);
        }
    }

    ngAfterViewInit() {
        this.updateSliderHeight();
        if (this.sliderContainer) {
            this.resizeObserver = new ResizeObserver(() => {
                window.requestAnimationFrame(() => {
                    if (!this.resizeObserver) return;
                    this.updateSliderHeight();
                });
            });
            this.resizeObserver.observe(this.sliderContainer.nativeElement);
        }
    }

    ngOnDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    @HostListener('window:resize')
    onResize() {
        this.updateSliderHeight();
    }

    @HostListener('wheel', ['$event'])
    onWheel(event: WheelEvent) {
        // Only trigger if we have multiple items and a significant scroll
        if (this.items.length <= 1) return;

        if (Math.abs(event.deltaY) > 20) {
            if (event.deltaY > 0 && this.canGoNext) {
                this.next();
                event.preventDefault();
            } else if (event.deltaY < 0 && this.canGoPrevious) {
                this.previous();
                event.preventDefault();
            }
        }
    }

    private updateSliderHeight() {
        if (this.sliderContainer) {
            const container = this.sliderContainer.nativeElement as HTMLElement;
            const items = container.querySelectorAll('.carousel-slider__item');

            // Map the exact offset of each item for precise scrolling
            this.itemOffsets = Array.from(items).map(item => (item as HTMLElement).offsetTop);

            if (items.length > 0) {
                const firstItem = items[0] as HTMLElement;
                if (items.length > 1) {
                    const secondItem = items[1] as HTMLElement;
                    // Distance between items (height + gap)
                    this.sliderHeight = secondItem.offsetTop - firstItem.offsetTop;
                } else {
                    this.sliderHeight = firstItem.offsetHeight;
                }
            } else {
                this.sliderHeight = container.offsetHeight;
            }
            this.cdr.markForCheck();
        }
    }

    getSliderTransform(): string {
        // Use the measured offset of the current item for exact positioning
        const offset = this.itemOffsets[this.currentIndex] !== undefined
            ? this.itemOffsets[this.currentIndex]
            : (this.currentIndex * this.sliderHeight);

        return `translateY(-${offset}px)`;
    }

    get totalItems(): number {
        return this.items.length;
    }

    get currentItem(): any {
        return this.items[this.currentIndex];
    }

    next(): void {
        if (this.canGoNext) {
            this.currentIndex++;
            this.cdr.markForCheck();
        }
    }

    previous(): void {
        if (this.canGoPrevious) {
            this.currentIndex--;
            this.cdr.markForCheck();
        }
    }

    get canGoPrevious(): boolean {
        return this.currentIndex > 0;
    }

    get canGoNext(): boolean {
        return this.currentIndex < this.items.length - 1;
    }
}
